import React, { useRef, useEffect, useState } from 'react';
import moment from 'moment';
import { find, trim, filter, findIndex, map, range } from 'ramda';
import imageExists from 'image-exists';
import {
    useParams, Link
} from "react-router-dom";
import { useSelector } from 'react-redux';
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab';
import { Document, Page } from "react-pdf";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import YouTube from 'react-youtube';

import './HorseView.scss';
import Favorite from './Favorite';
import EnhancedHorseEditForm from './EnhancedHorseEditForm';
import { selectAllHorses } from '../reducers/horsesReducer';
import { selectUpdates } from '../reducers/updatesReducer';
import ShareModal from './ShareModal';
import BonusContent from './BonusContent';
import HorseHighlight from './HorseHighlight';

const TabTitle = ({ title, icon, iconStyle='fas' }) => {
    return (
        <div style={{ textAlign: 'center', marginLeft: -16 }}>
            <FontAwesomeIcon style={{  verticalAlign:"middle" }} icon={[iconStyle, icon]} size='2x' />
            <span style={{ display: 'inline-block' }}>{title}</span>
        </div>
    );
};

const PDFView = ({ filePath, pdfWidth }) => {
    return (
        <div className="text-center" >
            <a className="mt-2 btn btn-secondary" href={filePath} download>
                <FontAwesomeIcon style={{  verticalAlign:"middle" }} icon={['fas', 'download']} size='2x' />    
            </a>
            <Document
                file={filePath}
            >
                <Page pageNumber={1} width={pdfWidth} scale={1} renderTextLayer={false} />
            </Document>
        </div>
    );
}

const genderMap = {
    C: 'COLT',
    F: 'FILLY',
    M: 'MARE',
}

// Hook
function useWindowSize() {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState({
      width: undefined,
    });
  
    useEffect(() => {
      // Handler to call on window resize
      function handleResize() {
        // Set window width/height to state
        if (windowSize.width === window.innerWidth) {
            return;
        }

        setWindowSize({
          width: window.innerWidth,
        });
      }
      
      // Add event listener
      window.addEventListener("resize", handleResize);
      
      // Call handler right away so state gets updated with initial window size
      handleResize();
      
      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
    }, []); // Empty array ensures that effect is only run on mount
  
    return windowSize;
}

const onEnd = (event) => {
    event.target.seekTo(0);
    event.target.stopVideo();
}

export default function HorseView() {
    const ref = useRef(null);
    const [key, setKey] = useState('pedigree');
    const windowSize = useWindowSize();
    let pdfWidth = windowSize.width > 845 ? 800 : windowSize.width - 45 ;
    const [hipImage, setHipImage] = useState(null);
    const { hip } = useParams();
    const [numPages, setNumPages] = useState(1);
    useEffect(() => {
        setKey('pedigree');
        window.scrollTo(0, 0)
    }, [hip]);
    const hipInt = parseInt(hip, 10);
    const { consignorID = null, consignor=null } = useParams();
    let consignorIDInt = consignorID ? parseInt(consignorID, 10) : null;
    consignorIDInt = consignor && consignor !== 'sale' ? parseInt(consignor) : consignorIDInt;
    const consignorObj = find((c) => parseInt(c.consignor_id, 10) === consignorIDInt)(global.drupalSettings.catalogues.consignors_contacts);
    const main_color = consignorObj && consignorObj.main_color || global.drupalSettings.catalogues.main_color;
    const secondary_color = consignorObj && consignorObj.secondary_color || global.drupalSettings.catalogues.secondary_color;
    const horse = useSelector(state => {
        const horses = state.horses;
        const currentHorseIndex = findIndex((h) => h.hip === hipInt)(horses);
        return currentHorseIndex !== -1 ? horses[currentHorseIndex] : null;
    });

    const [nextHorse, previousHorse] = useSelector(state => {
        const horses = selectAllHorses(state);
        let horsesToUse = horses;
        if (consignorIDInt) {
            horsesToUse = filter((h) => h.consignor_id == consignorIDInt)(horsesToUse);
        }
        const currentHorseIndex = findIndex((h) => h.hip === hipInt)(horsesToUse);
        const previousHorseIndex = currentHorseIndex <= 0 ? horsesToUse.length-1 : currentHorseIndex - 1;
        const nextHorseIndex = currentHorseIndex >= horsesToUse.length-1 ? 0 : currentHorseIndex + 1;
        return [horsesToUse[nextHorseIndex], horsesToUse[previousHorseIndex]];
    });
    const session = moment(global.drupalSettings.catalogues.sale_start_date, 'MM-DD-YYYY').isValid() ? moment(global.drupalSettings.catalogues.sale_start_date, 'MM-DD-YYYY') : null;
    let imagePath = null;
    if (horse) {
        imagePath = `https://www.fasigtipton.com/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/photos/${horse.hip}.jpg`;
        // imagePath = 'http://www.fasigtipton.com' + horse.enhancedhorsephoto_set[horse.enhancedhorsephoto_set.length - 1].photo;
    }
    useEffect(() => {
        if (imagePath) {
            imageExists(imagePath, (exists) => {
                if(exists) {

                    setHipImage(imagePath);
                }
                else {
                    setHipImage(global.drupalSettings.catalogues.ec_default_image.replace('http://','https://'));
                }
            });    
        }
    }, [horse]);
    const updates = useSelector(selectUpdates)

    if(!horse) {
        return (<></>);
    }

    // const horseConsignor = find((c) => parseInt(c.consignor_id, 10) === horse.consignor_id)(global.drupalSettings.catalogues.consignors_contacts);
    // const logoDisplay = !horseConsignor.logo ? null : (
    //     <div className="consignor-logo">
    //             <img className="img-fluid" src={horseConsignor.logo} alt="Consignor Logo" />
    //     </div>
    // );
    const logoDisplay = null;

    const prevHip = previousHorse ? previousHorse.hip : null;
    const nextHip = nextHorse ? nextHorse.hip : null;

    const horseUpdates = filter(update => update.horse.hip === horse.hip)(updates);

    const birthYear = moment(horse.year_of_birth, ["MM/DD/YYYY","YYYY-MM-DD"]).isValid() ? moment(horse.year_of_birth, ["MM/DD/YYYY","YYYY-MM-DD"]).format('YYYY') : null;
    // const pdf = `/1105/${horse.hip}.pdf`
    const pdf = `/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/${horse.hip}.pdf`


    const horseHeaderDisplay = (horse.sold_as_code === 'W' || horse.sold_as_code === 'Y' ?
        (`| ${horse.sire} ${genderMap[horse.sex]}`) :
        (trim(horse.name) ? `| ${trim(horse.name)}` : `| ${horse.sire} ${genderMap[horse.sex]}`)
    );
    
    const consignorInsightsTop = horse.enhanced_text_area ? (
        <>
            <div className="consignor-insights-header">
            </div>
            <div className="consignor-insights" dangerouslySetInnerHTML={{ __html: horse.enhanced_text_area }} />
        </>
    ): (
        <>
        </>
    );
    const horseUpdatesTab = horseUpdates.length > 0 && (
        <Tab eventKey="updates" title={<TabTitle title='Updates' icon="exclamation-circle" />}>
            <div className="row justify-content-center">
                <div className="tab-content-header">
                    Updates
                </div>
                <div className="break"></div>
                <div className="tab-content-wrapper updates" >
                    <pre>
                        {horseUpdates[0].update_text}
                    </pre>
                </div>
            </div>
        </Tab>

    );

    const bonusContentTab = (horse.enhanced_bonus_content) && (
        <Tab eventKey="bonusContent" title={<TabTitle title='Bonus Content' icon="star" />}>
            <div className="row justify-content-center">
                <div className="tab-content-header">
                    Bonus Content
                </div>
                <div className="break"></div>
                <div className="tab-content-wrapper">
                    <BonusContent horse={horse} />
                </div>
            </div>
        </Tab>
    );

    const consignorInsightsTab = horse.enhanced_text_area && (
        <Tab eventKey="consignorInsights" title={<TabTitle title='Consignor Insights' icon="search-plus"  />}>
            <div className="row justify-content-center">
                <div className="tab-content-header">
                    Consignor Insights
                </div>
                <div className="break"></div>
                <div className="tab-content-wrapper" dangerouslySetInnerHTML={{ __html: horse.enhanced_text_area }}>
                </div>
            </div>
        </Tab>
    );

    const stallionRegisterTab = horse.enhanced_stallion_register && (
        <Tab eventKey="stallion-register"
            title={<TabTitle title='Stallion Register' icon="registered" />}
        >
            <div className="row justify-content-center">
                <div className="tab-content-wrapper updates" >
                    Stallion Register
                </div>
            </div>
        </Tab>
    );

    const fixeXTab = horse.enhanced_5x_pedigree && (
        <Tab eventKey="fiveX"
            title={<TabTitle title='5X Pedigree' icon="html5" iconStyle="fab" />}
        >
            <div className="row justify-content-center">
                <div className="tab-content-wrapper updates" >
                    Stallion Register
                </div>
            </div>
        </Tab>
    );
    const virtualInspectionTab = horse.youtube_url && (
        <Tab eventKey="virtualInspection"
            title={<TabTitle title='Virtual Inspection' icon="video" />}
        >
            <div className="row justify-content-center">
                <div className="tab-content-wrapper updates" >
                    Virtual Inspection
                </div>
            </div>
        </Tab>
    );
    
    const hasStatCodes = ['RA'];
    const racingStatsTab = (hasStatCodes.includes(horse.sold_as_code)) && (
        <Tab eventKey="racingStats"
            title={<TabTitle title='Racing Stats' icon="etsy" iconStyle="fab" />}
        >
            <div className="row justify-content-center">
                <div className="tab-content-wrapper updates" >
                    Racing Stats
                </div>
            </div>
        </Tab>
    );

    //const ppPDF = `/1105/pp/${horse.hip}pp.pdf`
    const ppPDF = `/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/pp/${horse.hip}pp.pdf`
    const pastPerformancesTab = horse.pp_enabled && (
        <Tab eventKey="pp" title={<TabTitle title='Past Performances' icon="history" />}>
            <div className="row justify-content-center">
                <div ref={ref} className="tab-content-wrapper no-padding">
		       <PDFView filePath={ppPDF} pdfWidth={pdfWidth} />             
                </div>
            </div>
        </Tab>
    )

    //const ragPDF = `/1105/rag/${horse.hip}rag.pdf`
    const ragPDF = `/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/rag/${horse.hip}rag.pdf`
    const ragTab = horse.enhanced_ragozin && (
        <Tab eventKey="rag" title={<TabTitle title='Ragozin' icon="chart-line" />}>
            <div className="row justify-content-center">
                <div ref={ref} className="tab-content-wrapper no-padding">
		    <PDFView filePath={ragPDF} pdfWidth={pdfWidth} />             
                </div>
            </div>
        </Tab>
    )

    //const ragPDF = `/1105/rag/${horse.hip}rag.pdf`
    const tmPDF = `/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/tm/${horse.hip}tm.pdf`
    const tmTab = horse.enhanced_thoroughmanager && (
        <Tab eventKey="tm" title={<TabTitle title='Thoroughmanager' icon="trademark" />}>
            <div className="row justify-content-center">
                <div ref={ref} className="tab-content-wrapper no-padding">
		    <PDFView filePath={tmPDF} pdfWidth={pdfWidth} />             
                </div>
            </div>
        </Tab>
    )

    function onDocumentLoadSuccess({ numPages }) {
      setNumPages(numPages);
    }

    const pages = map((page) =>
        <Page pageNumber={page+1} width={pdfWidth} scale={1} key={page} />
    )(range(0, numPages));
    //const paPDF = `/1105/pa/${horse.hip}pa.pdf`
    const paPDF = `/catalogs/${session.year()}/${session.format('MM')}${session.format('DD')}/pa/${horse.hip}pa.pdf`

    const alanPorterAnalysisTab = horse.enhanced_alan_porter && (
        <Tab eventKey="pa" title={<TabTitle title='Alan Porter Analysis' icon="edit" />}>
            <div className="row justify-content-center">
                <div className="tab-content-wrapper no-padding">
        <div className="text-center" >
            <a className="mt-2 btn btn-secondary" href={paPDF} download>
                <FontAwesomeIcon style={{  verticalAlign:"middle" }} icon={['fas', 'download']} size='2x' />    
            </a>
            <Document
                file={paPDF}
                onLoadSuccess={onDocumentLoadSuccess}
            >
                        {pages}
            </Document>
        </div>
                </div>
            </div>
        </Tab>
    );

    const raceReplaysTab = horse.enhanced_race_replays && (
        <Tab eventKey="racereplays" title={<TabTitle title='Race Replays' icon="file-video" />}>
            <div className="row justify-content-center">
                <div className="tab-content-header">
                    Race Replays
                </div>
                <div className="break"></div>
                <div className="tab-content-wrapper-replays">
                    <iframe width="100%" height="100%" src={horse.enhanced_race_replays} frameBorder="0"  allow="fullscreen"></iframe>
                </div>
            </div>
        </Tab>
    )

    const featuredVideoTab = horse.enhanced_featured_video && (
        <Tab eventKey="featuredVideo"
            title={<TabTitle title='Featured Video' icon="video" />}
        >
            <div className="row justify-content-center">
                <div className="tab-content-header">
                        Featured Video
                    </div>
                    <div className="break"></div>
                    <div className="tab-content-wrapper">
                        <div
                            className="embed-responsive embed-responsive-16by9"
                            style={{
                                clear: 'both',
                            }}
                        >
                            <iframe width="560" height="315" src={horse.enhanced_featured_video} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen></iframe>
                        </div>
                    </div>
                </div>
        </Tab>
    );

    let photosTab = null;
    if (horse.generalhorsephoto_set.length > 0) {
        photosTab = (
            <Tab eventKey="photos"
                title={<TabTitle title='Photos' icon="camera-retro" />}
            >
                <div className="row justify-content-center">
                    <div className="tab-content-header">
                            Photos
                        </div>
                        <div className="break"></div>
                        <div className="tab-content-wrapper">
                            <div className="col-12" style={{ textAlign: 'center' }}>
                                <img src={horse.generalhorsephoto_set[0].photo} className='img-fluid' />
                            </div>
                        </div>
                    </div>
            </Tab>
        );
    }

    const styles = `
        .horse-tabs .nav-tabs .nav-link {
            background-color: ${main_color};
            color: ${secondary_color};
        }
        .horse-tabs .nav-tabs {
            background-color: ${main_color};
            border-bottom: 1px solid rgba(91, 92, 93, 0.25);
        }
        div.enhanced-content-button, div.bonus-content-button, div.bonus-content-link, div.bonus-content-social-link {
            background-color: ${main_color} !important;
        }

        div.enhanced-content-button:hover, div.bonus-content-button:hover, div.bonus-content-link:hover, div.bonus-content-social-link:hover {
            background-color: ${main_color} !important;
        }

        .horse-details a, .horse-details a:hover {
            color: ${main_color}
        }
        ${global.drupalSettings.catalogues.ec_bullet_code.length > 0 &&
            `.consignor-insights > ul {
                list-style: none;
            }

            .consignor-insights > ul > li:before {
                content: '${global.drupalSettings.catalogues.ec_bullet_code}';
                margin-left: -25px;
                float: left;
                line-height: 25px;
            }`
        }
    `;
    return (
        <>
            <div className='horse-view container-fluid'>
                <style>
                    {styles}
                </style>
                <div className="row top-header">
                    <div className="col">
                        <EnhancedHorseEditForm horse={horse} initialValues={horse} />
                        <h1>
                            <div className="hip-browser">
                                <Link to={`/${consignor}/${prevHip}`} style={{float: 'left'}}>
                                    <FontAwesomeIcon icon={['fas', 'angle-left']} />
                                </Link>
                                <Link to={`/${consignor}/${nextHip}`} style={{float: 'right'}}>
                                    <FontAwesomeIcon icon={['fas', 'angle-right']} />
                                </Link>
                            </div>
                            {global.drupalSettings.catalogues.sale_name}
                        </h1>
                    </div>
                </div>
                <div className="row horse-header">
                    <div className="col">
                        <h2>
                            <div className="share-button">
                                <ShareModal url={window.location.href} />
                            </div>
                            <Favorite horse={horse} />
                            HIP{' '}{horse.hip} {horseHeaderDisplay}
                        </h2>
                    </div>
                </div>
                <div className="row horse-highlight">
                    <HorseHighlight horse={horse} hipImage={hipImage} />
                    <div className="col-lg horse-details">
                        {logoDisplay}
                        <h3>
                            {(horse.sold_as_code !== 'W' &&  horse.sold_as_code !== 'Y' && trim(horse.name) ? `${trim(horse.name)}` : `${birthYear} ${horse.sex}.`)}
                        </h3>
                        <p className="sire-dam" style={{ fontSize: 26 }}>
                            {(horse.sold_as_code !== 'W' &&  horse.sold_as_code !== 'Y' && trim(horse.name) ? `${birthYear} ${horse.sex}.`: '')}
                        </p>
                        <p className="sire-dam">
                            {horse.sire} - {horse.dam}
                        </p>
                        <p className="consignor">
                            Consigned By: <Link to={`/consignor/${horse.consignor_id}`}>{horse.consignor}</Link>
                        </p>
                        {consignorInsightsTop}
                    </div>
                </div>
                <div className="row horse-tabs">
                    <div className="col">
                        <Tabs activeKey={key} id="tab-group" onSelect={
                            (eventKey, e) => {
                                if(eventKey === 'stallion-register') {
                                    window.open(horse.enhanced_stallion_register, '_blank');
                                } else if (eventKey === 'fiveX') {
                                    window.open(horse.enhanced_5x_pedigree, '_blank');
                                } else if (eventKey === 'virtualInspection') {
                                  window.open(horse.youtube_url, '_blank');
                                } else if (eventKey === 'racingStats') {
                                    window.open(`https://www.equibase.com/profiles/Results.cfm?type=Horse&refno=${horse.tjc_ref_num}&registry=T&rbt=TB`, '_blank');
                                } else {
                                        setKey(eventKey);
                                    }
                                }
                        }>
                            <Tab eventKey="pedigree" title={<TabTitle title='Pedigree' icon="file-alt" />}>
                                <div className="row justify-content-center">
                                    <div ref={ref} className="tab-content-wrapper no-padding">
		    <PDFView filePath={pdf} pdfWidth={pdfWidth} />             
                                    </div>
                                </div>
                            </Tab>
                            { pastPerformancesTab }
                            { horseUpdatesTab }
                            { ragTab }
                            { racingStatsTab }
                            { fixeXTab }
                            { stallionRegisterTab }
                            { alanPorterAnalysisTab }
                            { raceReplaysTab }
                            { photosTab }
                            { virtualInspectionTab }
                            { tmTab }
                            { featuredVideoTab }
                            { bonusContentTab }
                        </Tabs>
                    </div>
                </div>
                <div className="row horse-footer">
                    <div className="col">
                        <h1>
                            <div className="hip-browser">
                                <Link to={`/${consignor}/${prevHip}`} style={{float: 'left'}}>
                                    <FontAwesomeIcon icon={['fas', 'angle-left']} />
                                </Link>
                                <Link to={`/${consignor}/${nextHip}`} style={{float: 'right'}}>
                                    <FontAwesomeIcon icon={['fas', 'angle-right']} />
                                </Link>
                            </div>
                            {global.drupalSettings.catalogues.sale_name}

                        </h1>
                    </div>
                </div>
            </div>
        </>
    )

}
